import { Engine } from "@babylonjs/core/Engines/engine";
import { ColorCurves } from "@babylonjs/core/Materials/colorCurves";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";
import { Texture as BJSTexture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import "@babylonjs/core/Misc/dds"; //side effect for env texture loading
import "@babylonjs/core/Materials/Textures/Loaders/envTextureLoader"; //side effect for env texture loading
import { getDataPath, Log } from "../../utils";
import { ajax } from "../../utils/async";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { getFileExtension, isNotNullOrEmpty, isNullOrEmpty, joinPath } from "../../utils/string";
import { PbrMaterial } from "./PbrMaterial";
import { ResourceCache } from "./ResourceCache";
import { Texture } from "./Texture";
export class MaterialManager {
    get scene() {
        return (this.mScene);
    }
    /**
     * The environment mode controls which environment map/reflection texture will be used for PBR materials.
     * `Default` = `defaultHDR_512.env`
     * `Planning` = `planningHDR_512.env`
     */
    get environmentMode() {
        return (this.mEnvMode);
    }
    set environmentMode(pMode) {
        this.mEnvMode = pMode;
        this.mDefaultReflectionTexture = null;
    }
    /**
     * Url of a custom environment map/reflection texture which will be used instead of the predefined ones.
     * Only BabylonJs `.env` files are suppoted. If defined the `environmentMode` will be ignored.
     */
    get customEnvironmentUrl() {
        return (this.mCustomEnvUrl);
    }
    set customEnvironmentUrl(pUrl) {
        this.mCustomEnvUrl = pUrl;
        this.mDefaultReflectionTexture = null;
    }
    constructor(pApp) {
        this.onDisposeMaterial = (pMat, pCacheCleared) => {
            if (pMat == null)
                return;
            if (pCacheCleared)
                pMat.dispose(false, true);
            else
                pMat.dispose();
        };
        this.onDisposeTexture = (pTexture, pCacheCleared) => {
            if (pTexture == null)
                return;
            if (pTexture.isResource) {
                const tResTex = this.mResourceTextures.get(pTexture.id);
                if (tResTex != null) {
                    if (tResTex.blobUrl != null) {
                        URL.revokeObjectURL(tResTex.blobUrl);
                    }
                    this.mResourceTextures.delete(pTexture.id);
                }
            }
            pTexture.dispose();
        };
        this.mApp = pApp;
        this.mScene = pApp.scene;
        this.mEnvMode = "Default";
        this.mCustomEnvUrl = null;
        this.defaultMaterial = new PbrMaterial("default_mat", this);
        this.defaultMaterial.metallic = 0;
        this.defaultMaterial.roughness = 1;
        this.defaultMaterial.albedoColor = Color3.White();
        this.defaultMaterial.freeze();
        this.missingResourceTextures = new Set();
        this.pbrImageProcessing = new ImageProcessingConfiguration();
        this.pbrImageProcessing.toneMappingEnabled = true;
        this.pbrImageProcessing.colorCurvesEnabled = true;
        this.pbrImageProcessing.colorCurves = new ColorCurves();
        this.pbrImageProcessing.colorCurves.globalSaturation = 40;
        this.pbrImageProcessing.colorCurves.highlightsExposure = 52;
        this.pbrImageProcessing.colorCurves.midtonesExposure = 52;
        this.pbrImageProcessing.colorCurves.shadowsExposure = -50;
        this.mMaterials = new ResourceCache();
        this.mMaterials.onDisposeResource = this.onDisposeMaterial;
        this.mTextures = new ResourceCache();
        this.mTextures.onDisposeResource = this.onDisposeTexture;
        this.mResourceTextures = new Map();
        this.mDefaultReflectionTexture = null;
        this.mLastCacheCleanUp = Date.now();
        this.mLoadTexturesCount = 0;
        this.mLoadTexturesPromise = null;
        this.mLoadTexturesPromiseResolve = null;
        this.mMaxAnisotropy = this.mScene.getEngine().getCaps().maxAnisotropy;
    }
    addMaterial(pId, pMaterial) {
        if (isNullOrEmpty(pId) || pMaterial == null)
            return (false);
        if (!this.mMaterials.has(pId)) {
            //force correct id
            pMaterial.id = pId;
            this.mMaterials.add(pId, pMaterial);
            return (true);
        }
        return (false);
    }
    getMaterial(pId) {
        if (isNullOrEmpty(pId))
            return (null);
        const tMat = this.mMaterials.get(pId);
        return (tMat !== undefined ? tMat : null);
    }
    hasMaterial(pId) {
        if (isNullOrEmpty(pId))
            return (false);
        return (this.mMaterials.has(pId));
    }
    clear() {
        this.mLastCacheCleanUp = Date.now();
        this.mMaterials.clear();
        this.mTextures.clear();
        this.missingResourceTextures.clear();
        //should be empty already after mTextures.clear()
        this.mResourceTextures.clear();
    }
    getTexture(pId) {
        if (isNullOrEmpty(pId))
            return (null);
        const tTexture = this.mTextures.get(pId);
        return (tTexture !== undefined ? tTexture : null);
    }
    hasTexture(pId) {
        if (isNullOrEmpty(pId))
            return (false);
        return (this.mTextures.has(pId));
    }
    /**
     * Creates a persistent texture.
     * If a container is given, the url should be a path to a texture inside the container.
     */
    createResourceTexture(pId, pUrl, pHasAlpha = false, pContainer) {
        if (isNullOrEmpty(pId) || isNullOrEmpty(pUrl))
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        const tTexture = this.getTexture(pId);
        if (tTexture != null && !tTexture.isFallback)
            return (Promise.resolve(tTexture));
        if (!this.isTextureSupported(pUrl))
            return (Promise.reject(new Error("Unsupported texture file extension: " + pUrl)));
        let tBufferPromise;
        if (pContainer != null)
            tBufferPromise = pContainer.getFile(pUrl, true);
        else
            tBufferPromise = ajax("GET", pUrl, null, { responseType: "arraybuffer" });
        const tPromise = tBufferPromise.then((pBuffer) => {
            let tType = "jpg";
            if (getFileExtension(pUrl).toLowerCase() === ".png")
                tType = "png";
            return (this._internalCreateResourceTexture(pId, pUrl, tType, pBuffer, pHasAlpha, tTexture));
        });
        return (tPromise);
    }
    /** Returnes the data of a resource texture */
    getResourceTextureData(pTextureId) {
        const tData = this.mResourceTextures.get(pTextureId);
        return (tData === undefined ? null : tData);
    }
    /**
     * Creates a runtime only texture.
     */
    createTexture(pId, pUrl, pHasAlpha = false) {
        if (isNullOrEmpty(pId) || isNullOrEmpty(pUrl))
            throw new Error(ERR_INVALID_ARGUMENT);
        //check cache first
        let tTexture = this.getTexture(pId);
        if (tTexture != null)
            return (tTexture);
        if (!this.isTextureSupported(pUrl))
            throw new Error("Unsupported texture file extension: " + pUrl);
        tTexture = this._internalCreateTexture(pId, pUrl, pUrl, false, pHasAlpha ?
            Engine.TEXTUREFORMAT_RGBA : Engine.TEXTUREFORMAT_RGB);
        tTexture.hasAlpha = pHasAlpha;
        return (tTexture);
    }
    waitUntilTexturesLoaded() {
        if (this.mLoadTexturesPromise == null)
            return (Promise.resolve());
        else
            return (this.mLoadTexturesPromise);
    }
    getDefaultReflectionTexture() {
        if (this.mDefaultReflectionTexture == null) {
            this.startLoadTexture();
            let tEnvMap = this.mCustomEnvUrl;
            if (isNullOrEmpty(tEnvMap)) {
                tEnvMap = joinPath(getDataPath(), "core/textures/");
                tEnvMap += this.mEnvMode === "Default" ? "defaultHDR_512.env" : "planningHDR_512.env";
            }
            this.mDefaultReflectionTexture = new CubeTexture(tEnvMap, this.mScene, null, false, null, () => {
                this.mApp.viewer.requestRenderFrame(); //render frame after delayed texture loading
                this.finishLoadTexture();
            }, () => {
                Log.error("Failed to load default reflection texture.");
                this.finishLoadTexture();
            }, undefined, true, undefined, true);
            //this.mDefaultReflectionTexture.rotationY = -utils.math.PI_DIV2;
        }
        return (this.mDefaultReflectionTexture);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _cleanUpCache(pImmediately) {
        const tNow = Date.now();
        if (pImmediately || tNow - this.mLastCacheCleanUp > MaterialManager.CACHE_CLEANUP_INTERVAL) {
            this.mLastCacheCleanUp = tNow;
            const tMaxTimeUnused = pImmediately ? 0 : MaterialManager.CACHE_CLEANUP_INTERVAL;
            this.cleanUpMaterialCache(tMaxTimeUnused);
            this.cleanUpTextureCache(tMaxTimeUnused);
        }
    }
    /** @internal */
    _getResourceTextures() {
        return (this.mResourceTextures);
    }
    /** @internal */
    _internalCreateResourceTexture(pId, pName, pType, pBuffer, pHasAlpha, pOldTexture = null) {
        const tBlob = new Blob([pBuffer], { type: pType === "jpg" ? "image/jpeg" : "image/png" });
        const tBlobUrl = URL.createObjectURL(tBlob);
        const tTexture = this._internalCreateTexture(pId, pName, tBlobUrl, true, pHasAlpha ?
            Engine.TEXTUREFORMAT_RGBA : Engine.TEXTUREFORMAT_RGB);
        tTexture.hasAlpha = pType === "png" ? pHasAlpha : false;
        const tResTex = {
            data: pBuffer,
            blobUrl: tBlobUrl,
            type: pType
        };
        this.mResourceTextures.set(pId, tResTex);
        //remove from missing list
        this.missingResourceTextures.delete(pId);
        //texture got replaced, this can happen if the old texture is a fallback texture
        //update all materials, to use the new texture
        if (pOldTexture != null) {
            this.mMaterials.forEach((pId, pMat) => {
                pMat.replaceTexture(pOldTexture, tTexture);
            });
            //dispose old texture
            pOldTexture.dispose();
        }
        return (tTexture);
    }
    /** @internal */
    _internalCreateTexture(pId, pName, pUrl, pIsResource, pFormat, pSamplingMode = BJSTexture.TRILINEAR_SAMPLINGMODE) {
        //create/load the texture
        this.startLoadTexture();
        const tTexture = new Texture(pUrl, this.mScene, () => {
            this.mApp.viewer.requestRenderFrame(); //render frame after delayed texture loading
            this.finishLoadTexture();
        }, () => {
            Log.error("Failed to load texture: " + pUrl);
            this.finishLoadTexture();
        }, pSamplingMode, pFormat);
        tTexture.anisotropicFilteringLevel = this.mMaxAnisotropy;
        tTexture.id = pId;
        if (isNotNullOrEmpty(pName))
            tTexture.name = pName;
        tTexture.isResource = pIsResource;
        //add to cache
        this.mTextures.add(pId, tTexture);
        return (tTexture);
    }
    isTextureSupported(pUrl) {
        if (isNullOrEmpty(pUrl))
            return (false);
        let tExt = getFileExtension(pUrl);
        if (isNullOrEmpty(tExt))
            return (false);
        tExt = tExt.toLowerCase();
        if (tExt != ".png" && tExt != ".jpg")
            return (false);
        return (true);
    }
    cleanUpMaterialCache(pMaxTimeUnunsed) {
        //collect used materials
        const tUsedMaterials = new Set();
        for (const tMesh of this.mApp.model.meshInstances) {
            if (tMesh.material != null && tMesh.material instanceof PbrMaterial) {
                const tId = tMesh.material.id;
                if (isNotNullOrEmpty(tId))
                    tUsedMaterials.add(tId);
            }
        }
        //remove unused
        this.mMaterials.removeUnused(tUsedMaterials, pMaxTimeUnunsed);
    }
    cleanUpTextureCache(pMaxTimeUnunsed) {
        const tUsedTextures = new Set();
        const addTexture = function (pTexture) {
            if (pTexture == null)
                return;
            if (pTexture instanceof Texture && isNotNullOrEmpty(pTexture.id))
                tUsedTextures.add(pTexture.id);
        };
        //collect used textures
        for (const tMat of this.mScene.materials) {
            if (tMat instanceof PbrMaterial) {
                tMat.collectTextures(addTexture);
            }
        }
        //remove unused
        this.mTextures.removeUnused(tUsedTextures, pMaxTimeUnunsed);
    }
    startLoadTexture() {
        this.mLoadTexturesCount++;
        if (this.mLoadTexturesPromise == null) {
            this.mLoadTexturesPromise = new Promise((pResolve) => {
                this.mLoadTexturesPromiseResolve = pResolve;
            });
        }
    }
    finishLoadTexture() {
        if (this.mLoadTexturesCount === 0)
            return;
        this.mLoadTexturesCount--;
        if (this.mLoadTexturesCount === 0) {
            this.mLoadTexturesPromiseResolve();
            this.mLoadTexturesPromise = null;
            this.mLoadTexturesPromiseResolve = null;
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
MaterialManager.CACHE_CLEANUP_INTERVAL = 120000;
